import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import Header from "../components/Header";
import TextBox from "../components/TextBox";
import { registrationSchema } from "../schemas";
import { hostSignupReset } from "../store/slices/hostSlices";
import { hostSignup } from "../store/thunks/hostActions";

const AffiliateSignupScreen = () => {
  const { code } = useParams(); // from URL
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [captchaValue, setCaptchaValue] = useState(null);
  const [signupError, setSignupError] = useState();
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [affiliateCode, setAffiliateCode] = useState(code || "");

  const isCodeLocked = !!code;

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    isManager: "",
    selfOnboarding: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      zipCode: "",
    },
  };

  const { values, errors, touched, handleBlur, handleChange, getIn } = useFormik({
    initialValues,
    validationSchema: registrationSchema,
  });

  const { loading, success, error } = useSelector((state) => state.hostSignUp);
  const { hostInfo } = useSelector((state) => state.host);

  const onCaptchaChange = (token) => setCaptchaValue(token);

  const handleSignUp = () => {
    if (!captchaValue) {
      setSignupError("Please verify that you are not a robot.");
      return;
    }

    if (values.isManager) {
      values.company = companyName;
    }

    if (errors.address && Object.keys(errors.address).length > 0) {
      setSignupError(errors.address[Object.keys(errors.address)[0]]);
      return;
    }

    if (Object.keys(errors).length > 0) {
      setSignupError(errors[Object.keys(errors)[0]]);
      return;
    }

    if (values.password !== confirmPassword) {
      setSignupError("Password does not match");
      return;
    }

    dispatch(
      hostSignup({
        ...values,
        captchaResponse: captchaValue,
        affiliateCode: affiliateCode.trim() || null,
      })
    );
  };

  useEffect(() => {
    if (hostInfo) {
      navigate("/host");
    }
    if (success) {
      navigate("/please-verify");
      dispatch(hostSignupReset());
    }
    if (error) {
      setTimeout(() => dispatch(hostSignupReset()), 2000);
    }
  }, [success, navigate, hostInfo, error]);

  useEffect(() => {
    if (signupError) {
      const timer = setTimeout(() => setSignupError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [signupError]);

  return (
    <div className="flex flex-col min-h-screen w-full h-fit">
      <Header />
      {(error || signupError) && (
        <Alert severity="error" style={{ width: "90%", margin: "auto", marginTop: 10 }}>
          <strong>{error || signupError}</strong>
        </Alert>
      )}

      <div className="flex justify-center bg-white h-full w-full flex-grow">
        <div className="laptop:w-[450px] tabletOnly:w-[400px] mobile:w-[320px] text-center pb-10">
          <div className="top mt-5">
            <h2 className="font-bold text-lg">Sign Up</h2>
            <p className="text-base2 text-light">
              Already have an account?{" "}
              <label className="text-primary">
                <Link to={"/login"}>Sign In</Link>
              </label>
            </p>
          </div>

          <div className="form mt-[50px]">
            <div className="flex items-center gap-x-2">
              <TextBox label="First Name" name="firstName" value={values.firstName} onChange={handleChange} errors={errors} handleBlur={handleBlur} />
              <TextBox label="Last Name" name="lastName" value={values.lastName} onChange={handleChange} errors={errors} handleBlur={handleBlur} />
            </div>

            <TextBox label="Email" name="email" value={values.email} onChange={handleChange} errors={errors} handleBlur={handleBlur} />
            <TextBox label="Mobile Number" name="phoneNumber" value={values.phoneNumber} onChange={handleChange} errors={errors} handleBlur={handleBlur} />

            <div className="flex flex-gap-5 w-full justify-between py-5">
              <label>
                Are you a Property Manager{" "}
                {errors.isManager && <span className="text-[red] inline-block pl-1">*</span>}
              </label>
              <div className="yes_no flex">
                <div className="mx-5">
                  <input type="radio" name="isManager" value="true" onChange={handleChange} onBlur={handleBlur} />
                  <label>Yes</label>
                </div>
                <div className="mx-5">
                  <input type="radio" name="isManager" value="false" onChange={handleChange} onBlur={handleBlur} />
                  <label>No</label>
                </div>
              </div>
            </div>

            {values.isManager === "true" && (
              <TextBox label="Company Name" name="companyName" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
            )}

            <div className="flex items-center gap-x-7 mobile:flex-col">
              <TextBox label="Address Line 1" name="address.line1" type="text" value={values.address.line1} onChange={handleChange} errors={errors} getIn={getIn} handleBlur={handleBlur} />
              <TextBox label="Address Line 2" name="address.line2" type="text" value={values.address.line2} onChange={handleChange} errors={errors} getIn={getIn} handleBlur={handleBlur} />
            </div>

            <div className="flex items-center gap-x-7 mobile:flex-col">
              <TextBox label="City" name="address.city" type="text" value={values.address.city} onChange={handleChange} errors={errors} getIn={getIn} handleBlur={handleBlur} />
              <TextBox label="State" name="address.state" type="text" value={values.address.state} onChange={handleChange} errors={errors} getIn={getIn} handleBlur={handleBlur} />
              <TextBox label="Zip Code" name="address.zipCode" type="text" value={values.address.zipCode} onChange={handleChange} errors={errors} getIn={getIn} handleBlur={handleBlur} />
            </div>

            <div className="flex flex-col items-center w-full py-5">
              <label className="mb-2 font-bold">
                Are you self-signing up so we can schedule an onboarding call?
              </label>
              <div className="flex justify-center">
                <div className="mx-5">
                  <input type="radio" name="selfOnboarding" value="true" onChange={handleChange} onBlur={handleBlur} checked={values.selfOnboarding === "true"} required />
                  <label>Yes</label>
                </div>
                <div className="mx-5">
                  <input type="radio" name="selfOnboarding" value="false" onChange={handleChange} onBlur={handleBlur} checked={values.selfOnboarding === "false"} required />
                  <label>No</label>
                </div>
              </div>
            </div>

            <TextBox label="Password" name="password" type="password" value={values.password} onChange={handleChange} errors={errors} handleBlur={handleBlur} />
            <TextBox label="Confirm Password" name="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />

            <TextBox
            label="Affiliate Code"
            name="affiliateCode"
            value={affiliateCode}
            onChange={(e) => !isCodeLocked && setAffiliateCode(e.target.value)}
            disabled={isCodeLocked}
            readOnly={isCodeLocked}
            />


            <ReCAPTCHA sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq" onChange={onCaptchaChange} className="my-4" />

            <button onClick={handleSignUp} className="bg-primary w-full h-[50px] rounded-[40px] mt-10 text-white font-bold">
              {loading ? <CircularProgress color="inherit" size={"20px"} /> : "Sign Up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateSignupScreen;
